<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import Card from 'primevue/card';
import Toolbar from 'primevue/toolbar';
import { createModel } from '@kp/js-model';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { errToToast, reallyCancel } from '@/helpers/tools';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router';
import { TYPES } from '@/models/EchCode';
import { getI18n } from '@/helpers/i18n';
import KpButton from '@/components/KpButton.vue';
import KpNumberField from '@/components/KpNumberField.vue';
import { ReduktionWaermeerzeuger } from '@/models/ReduktionWaermeerzeuger';
import { createValidator, createValidators, validateNotEmpty, validateNumberBetween } from '@/helpers/model_validators';
import KpTextAreaField from '@/components/KpTextAreaField.vue';
import KpEchCodeSelect from '@/components/KpEchCodeSelect.vue';
import KpWaermeerzeugerHeizungSelect from '@/components/KpWaermeerzeugerHeizungSelect.vue';

const props = defineProps<{
    // id is injected via router:
    id: number | 'new';
}>();

const router = useRouter();
const toast = useToast();
const record: Ref<ReduktionWaermeerzeuger | null> = ref(null);
const saving = ref(false);

const { allValid, validators } = createValidators({
    bauperiode: createValidator(
        computed(() => record.value?.bauperiode_id),
        [validateNotEmpty],
    ),
    reduktion: createValidator(
        computed(() => record.value?.reduktion || null),
        [validateNotEmpty, validateNumberBetween(0, 100)],
    ),
    waermeerzeuger_heizung: createValidator(
        computed(() => record.value?.waermeerzeuger_heizung_id),
        [validateNotEmpty],
    ),
});

onMounted(() => {
    loadRecord(props.id);
});

onBeforeRouteLeave(() => {
    return reallyCancel(record?.value);
});

onBeforeRouteUpdate((to, from) => {
    if (to.params.id !== from.params.id) {
        loadRecord(to.params.id === 'new' ? 'new' : Number(to.params.id));
    }
});

async function loadRecord(id: number | 'new') {
    if (id === 'new') {
        record.value = createModel(ReduktionWaermeerzeuger);
    } else {
        try {
            const rec = await ReduktionWaermeerzeuger.loadById(id);
            if (rec) {
                record.value = rec;
            } else {
                record.value = null;
                throw new Error(getI18n().t('record_not_found'));
            }
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

async function onSave() {
    if (record?.value === null) {
        return;
    }
    saving.value = true;
    try {
        const isNew = !Number(record.value.id);
        await record.value.save();
        record.value.commit();
        toast.add({
            severity: 'success',
            detail: getI18n().t('datensatz_gespeichert'),
            life: 1000,
        });
        if (isNew) {
            router.push({ name: 'bg_geb_reduktion_waermeerzeuger_edit', params: { id: record.value.id } });
        }
    } catch (e) {
        errToToast(e, toast);
    } finally {
        saving.value = false;
    }
}

function onCancel() {
    if (reallyCancel(record?.value)) {
        record?.value?.rollback();
        router.push({ name: 'bg_geb_reduktion_waermeerzeuger' });
    }
}
</script>
<template>
    <div v-if="!record" class="flex justify-content-center">
        <ProgressSpinner></ProgressSpinner>
    </div>
    <div v-else>
        <Card class="kp-max-content-width">
            <template #header>
                <Toolbar class="toolbar">
                    <template #start>
                        <h2>
                            <i class="fas fa-user"></i> {{ $t('reduktion_waermeerzeuger') }}:
                            {{ record.id || $t('new') }}
                        </h2>
                    </template>
                    <template #end>
                        <div class="flex gap-1">
                            <KpButton
                                severity="success"
                                icon="far fa-floppy-disk"
                                v-tooltip="$t('speichern')"
                                :loading="saving"
                                :disabled="!allValid"
                                @click="onSave"
                            />
                            <KpButton
                                severity="secondary"
                                icon="far fa-xmark"
                                v-tooltip="$t('abbrechen')"
                                :disabled="saving"
                                @click="onCancel"
                            />
                        </div>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="formgrid grid align-items-start">
                    <KpEchCodeSelect
                        v-model="record.bauperiode_id"
                        class="col-6"
                        fluid
                        :type="TYPES.bauperiode"
                        :label="$t('bauperiode')"
                        :invalid="!validators.bauperiode.isValid.value"
                        :error="validators.bauperiode.msg.value"
                    ></KpEchCodeSelect>
                    <KpWaermeerzeugerHeizungSelect
                        v-model="record.waermeerzeuger_heizung_id"
                        :label="$t('waermeerzeuger_heizung')"
                        class="col-6"
                        fluid
                        :invalid="!validators.waermeerzeuger_heizung.isValid.value"
                        :error="validators.waermeerzeuger_heizung.msg.value"
                    ></KpWaermeerzeugerHeizungSelect>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpNumberField
                        v-model="record.reduktion"
                        :label="$t('reduktion')"
                        class="col-6"
                        fluid
                        :invalid="!validators.reduktion.isValid.value"
                        :error="validators.reduktion.msg.value"
                    ></KpNumberField>
                    <div class="col-6" fluid></div>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpTextAreaField
                        v-model="record.bemerkung"
                        :label="$t('bemerkung')"
                        class="col-12"
                        fluid
                    ></KpTextAreaField>
                </div>
            </template>
        </Card>
    </div>
</template>

<style lang="css" scoped>
.toolbar {
    border: none;
    border-bottom: 1px solid var(--p-toolbar-border-color);
    h2 {
        margin: 0;
    }
}
</style>
